import { useCallback, useEffect, useState } from "react";
import { Header } from "../components/Header";
import { OnboardingModal } from "../components/OnboardingModal";
import { Sidebar } from "../components/Sidebar";
import { Calendar } from "../features/calendar/Calendar";
import { Dashboard } from "../features/dashboard/Dashboard";
import { Meetings } from "../features/meetings/Meetings";
import { Privacy } from "../features/privacy/Privacy";
import { Settings } from "../features/settings/Settings";
import { Search } from "../features/search/Search";
import { Skills } from "../features/skills/Skills";
import { Translate } from "../features/translate/Translate";
import {
  initializeStorage,
  listMeetings,
  listSkills,
  listTodos,
  loadSettings,
  saveMeeting,
  saveTodo,
} from "../lib/storage";
import { loadApiMode, saveApiMode } from "../lib/onboarding";
import type {
  ApiMode,
  AppSettings,
  Meeting,
  Page,
  Skill,
  TodoItem,
} from "../types";

export function App(): React.JSX.Element {
  const [page, setPage] = useState<Page>("dashboard");
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [settings, setSettings] = useState<AppSettings>(() => loadSettings());
  const [apiMode, setApiMode] = useState<ApiMode | null>(() => loadApiMode());
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(
    null,
  );
  const [startNewMeeting, setStartNewMeeting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState("");

  const refresh = useCallback(async (): Promise<void> => {
    const [nextMeetings, nextSkills, nextTodos] = await Promise.all([
      listMeetings(),
      listSkills(),
      listTodos(),
    ]);
    setMeetings(nextMeetings);
    setSkills(nextSkills);
    setTodos(nextTodos);
  }, []);

  useEffect(() => {
    void initializeStorage()
      .then(refresh)
      .catch(() => setFatalError("Local browser storage is unavailable."))
      .finally(() => setLoading(false));
  }, [refresh]);
  useEffect(() => {
    const media = matchMedia("(prefers-color-scheme: dark)");
    const apply = (): void => {
      const dark =
        settings.theme === "dark" ||
        (settings.theme === "system" && media.matches);
      document.documentElement.dataset.theme = dark ? "dark" : "light";
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [settings.theme]);

  const navigate = (next: Page): void => {
    setPage(next);
    setSelectedMeetingId(null);
    setStartNewMeeting(false);
  };
  const newMeeting = (): void => {
    setStartNewMeeting(true);
    setSelectedMeetingId(null);
    setPage("meetings");
  };
  const openMeeting = (id: string): void => {
    setSelectedMeetingId(id);
    setStartNewMeeting(false);
    setPage("meetings");
  };
  const toggleAction = async (
    meetingId: string,
    actionId: string,
  ): Promise<void> => {
    const meeting = meetings.find((item) => item.id === meetingId);
    if (!meeting) return;
    const updated: Meeting = {
      ...meeting,
      updatedAt: new Date().toISOString(),
      summary: {
        ...meeting.summary,
        actionItems: meeting.summary.actionItems.map((item) =>
          item.id === actionId ? { ...item, done: !item.done } : item,
        ),
      },
    };
    await saveMeeting(updated);
    await refresh();
  };
  const toggleTodo = async (todo: TodoItem): Promise<void> => {
    await saveTodo({ ...todo, completed: !todo.completed });
    await refresh();
  };

  if (loading)
    return (
      <div className="boot-screen">
        <div className="brand-mark">D</div>
        <span>Loading Deskflow…</span>
      </div>
    );
  if (fatalError)
    return (
      <div className="boot-screen error-screen">
        <strong>{fatalError}</strong>
        <p>Enable browser storage and reload the page.</p>
      </div>
    );

  return (
    <div className="app-shell">
      <Sidebar page={page} onNavigate={navigate} />
      <main className="main-content">
        <Header page={page} onNewMeeting={newMeeting} />
        {page === "dashboard" && (
          <Dashboard
            meetings={meetings}
            todos={todos}
            onOpenMeeting={openMeeting}
            onNewMeeting={newMeeting}
            onViewMeetings={() => navigate("meetings")}
            onToggleAction={(meetingId, actionId) =>
              void toggleAction(meetingId, actionId)
            }
            onToggleTodo={(todo) => void toggleTodo(todo)}
          />
        )}
        {page === "meetings" && (
          <Meetings
            key={`${selectedMeetingId ?? "list"}-${startNewMeeting}`}
            meetings={meetings}
            initialMeetingId={selectedMeetingId}
            startNew={startNewMeeting}
            onRefresh={refresh}
            onTodosChanged={refresh}
            onClearIntent={() => {
              setStartNewMeeting(false);
              setSelectedMeetingId(null);
            }}
          />
        )}
        {page === "calendar" && <Calendar todos={todos} onRefresh={refresh} />}
        {page === "translate" && <Translate />}
        {page === "skills" && <Skills skills={skills} />}
        {page === "search" && <Search />}
        {page === "settings" && (
          <Settings settings={settings} onChange={setSettings} />
        )}
        {page === "privacy" && (
          <Privacy
            meetingCount={meetings.length}
            todoCount={todos.length}
            onDeleted={async () => {
              setSettings(loadSettings());
              setApiMode(loadApiMode());
              await refresh();
            }}
          />
        )}
      </main>
      {apiMode === null && (
        <OnboardingModal
          onChooseDemo={() => {
            saveApiMode("demo");
            setApiMode("demo");
          }}
        />
      )}
    </div>
  );
}
